import { Component, StrictMode, Suspense, lazy, useEffect, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell, RequireAccess } from './components/AppShell';
import { LoginScreen } from './components/LoginScreen';
import { ErrorState, FeedbackProvider, Spinner } from './components/ui';
import { applyStoredDisplayTheme } from './components/ThemeControls';
import { useAuth } from './store/auth';
import { PosScreen } from './pages/PosScreen';
import { ReturnsPage } from './pages/ReturnsPage';
import { CustomerDisplay } from './pages/CustomerDisplay';
import { useSaleAttempt } from './store/sale-attempt';
import { useCart } from './store/cart';
import { preloadStoredLanguage, tr, useT } from './i18n';
import './index.css';
import './fonts/tamil.css';

/**
 * The till and counter returns ship in the main bundle. Back-office screens load on demand so
 * the till starts sooner, and are fetched in the background once someone signs in (see
 * `prefetchBackOffice`), so opening them stays instant and survives a link that drops later.
 */
const backOffice = {
  products: () => import('./pages/ProductsPage').then(module => ({ default: module.ProductsPage })),
  inventory: () => import('./pages/InventoryPage').then(module => ({ default: module.InventoryPage })),
  purchasing: () => import('./pages/PurchasingPage').then(module => ({ default: module.PurchasingPage })),
  customers: () => import('./pages/CustomersPage').then(module => ({ default: module.CustomersPage })),
  customerProfile: () => import('./pages/CustomerProfilePage').then(module => ({ default: module.CustomerProfilePage })),
  collections: () => import('./pages/CollectionsPage').then(module => ({ default: module.CollectionsPage })),
  import: () => import('./pages/ImportPage').then(module => ({ default: module.ImportPage })),
  reports: () => import('./pages/ReportsPage').then(module => ({ default: module.ReportsPage })),
  admin: () => import('./pages/AdminPage').then(module => ({ default: module.AdminPage })),
  readiness: () => import('./pages/ReadinessPage').then(module => ({ default: module.ReadinessPage })),
  platform: () => import('./pages/PlatformPage').then(module => ({ default: module.PlatformPage })),
};
const ProductsPage = lazy(backOffice.products);
const InventoryPage = lazy(backOffice.inventory);
const PurchasingPage = lazy(backOffice.purchasing);
const CustomersPage = lazy(backOffice.customers);
const CustomerProfilePage = lazy(backOffice.customerProfile);
const CollectionsPage = lazy(backOffice.collections);
const ImportPage = lazy(backOffice.import);
const ReportsPage = lazy(backOffice.reports);
const AdminPage = lazy(backOffice.admin);
const ReadinessPage = lazy(backOffice.readiness);
const PlatformPage = lazy(backOffice.platform);

/** Warms every back-office chunk after the till is up. A failure here is harmless: the route retries on open. */
function prefetchBackOffice(): () => void {
  const timer = window.setTimeout(() => {
    for (const load of Object.values(backOffice)) void load().catch(() => undefined);
  }, 2_000);
  return () => window.clearTimeout(timer);
}

/**
 * Query defaults tuned for a till, not for a dashboard: data on this screen is read once,
 * scanned past, and must never be re-fetched while a sale is being rung up.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry a 4xx: a refused permission or a validation error will not fix itself,
        // and retrying only delays the message the cashier needs.
        const status = (error as { status?: number }).status;
        if (typeof status === 'number' && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

useAuth.subscribe((state, previous) => {
  const key = (p: typeof state.principal) => [p?.userId, p?.storeId, p?.terminalId].join(':');
  if (key(state.principal) !== key(previous.principal)) {
    void queryClient.cancelQueries(); queryClient.clear(); useCart.getState().clear();
  }
});

function Root() {
  const { principal, initialising, restore } = useAuth();
  // The customer display faces the queue: it follows the till over a BroadcastChannel and is
  // never signed in, so it renders before (and instead of) the sign-in gate.
  const isDisplay = useLocation().pathname === '/display';
  const pending = useSaleAttempt(s => s.pending);
  const t = useT();
  const pathname = useLocation().pathname;

  // A refresh at the till must not bounce the cashier back to the login screen.
  useEffect(() => {
    if (!isDisplay) void restore();
  }, [restore, isDisplay]);

  const signedIn = principal !== null;
  useEffect(() => (signedIn && !isDisplay ? prefetchBackOffice() : undefined), [signedIn, isDisplay]);

  if (isDisplay) return <CustomerDisplay />;

  if (initialising) {
    return (
      <div className="flex h-full items-center justify-center gap-3 bg-ink text-sm text-on-ink-2">
        <Spinner label={t('app.loading')} />
        {t('app.loading')}
      </div>
    );
  }

  if (!principal) return <LoginScreen />;
  if (pending && pathname !== '/') return <Navigate to="/" replace />;

  return (
    <AppShell key={[principal.userId, principal.storeId, principal.terminalId].join(':')}>
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/" element={<PosScreen />} />
          <Route path="/products" element={<RequireAccess path="/products"><ProductsPage /></RequireAccess>} />
          <Route path="/inventory" element={<RequireAccess path="/inventory"><InventoryPage /></RequireAccess>} />
          <Route path="/purchasing" element={<RequireAccess path="/purchasing"><PurchasingPage /></RequireAccess>} />
          <Route path="/customers" element={<RequireAccess path="/customers"><CustomersPage /></RequireAccess>} />
          <Route path="/customers/:id" element={<RequireAccess path="/customers"><CustomerProfilePage /></RequireAccess>} />
          <Route path="/collections" element={<RequireAccess path="/collections"><CollectionsPage /></RequireAccess>} />
          <Route path="/import" element={<RequireAccess path="/import"><ImportPage /></RequireAccess>} />
          <Route path="/returns" element={<RequireAccess path="/returns"><ReturnsPage /></RequireAccess>} />
          <Route path="/reports" element={<RequireAccess path="/reports"><ReportsPage /></RequireAccess>} />
          <Route path="/admin" element={<RequireAccess path="/admin"><AdminPage /></RequireAccess>} />
          <Route path="/readiness" element={<RequireAccess path="/readiness"><ReadinessPage /></RequireAccess>} />
          <Route path="/platform" element={<RequireAccess path="/platform"><PlatformPage /></RequireAccess>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

/** Last line of defence: a screen that throws shows a message and a way back, not a white page. */
class RootBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled UI error', error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="flex h-full items-center justify-center bg-slate-100">
          <div className="panel max-w-md">
            <ErrorState
              title={tr('app.crashed')}
              message={tr('app.crashedDetail', { message: this.state.error.message || tr('common.error.unexpected') })}
              onRetry={() => window.location.reload()}
            />
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

applyStoredDisplayTheme();

const container = document.getElementById('root');
if (!container) throw new Error('The #root element is missing from index.html.');

// A browser that remembers Tamil fetches its tables before the first paint; an English store
// never downloads that chunk at all.
void preloadStoredLanguage().then(() => {
  createRoot(container).render(
    <StrictMode>
      <RootBoundary>
        <QueryClientProvider client={queryClient}>
          <FeedbackProvider>
            <BrowserRouter>
              <Root />
            </BrowserRouter>
          </FeedbackProvider>
        </QueryClientProvider>
      </RootBoundary>
    </StrictMode>,
  );
});
